import { useEffect, useState } from "react";
import { getSupabase } from "../lib/supabase";
import { uploadTestimonialImage } from "../utils/uploadTestimonialImage";

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    company_name: "",
    service: "",
    remarks: "",
    case_study: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    company_name: "",
    service: "",
    remarks: "",
    case_study: "",
  });
  const [editLogoFile, setEditLogoFile] = useState(null);

  /* ================= FETCH ================= */
  async function fetchTestimonials() {
    setLoading(true);
    setError("");

    const supabase = await getSupabase();

    const { data, error } = await supabase
      .schema("api")
      .from("testimonials")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) setError("Failed to load testimonials.");
    else setItems(data || []);

    setLoading(false);
  }

  useEffect(() => {
    fetchTestimonials();
  }, []);

  /* ================= LOGO UPLOAD ================= */

  /* ================= CREATE ================= */
  async function handleCreate(e) {
    e.preventDefault();
    setSubmitting(true);

    try {
      const supabase = await getSupabase();
      let logoPath = null;
      if (logoFile) {
        logoPath = await uploadLogo(logoFile, form.company_name);
      }

      const { error } = await supabase
        .schema("api")
        .from("testimonials")
        .insert({
          ...form,
          company_logo_url: logoPath,
          is_published: false,
        });

      if (error) throw error;

      setForm({
        company_name: "",
        service: "",
        remarks: "",
        case_study: "",
      });
      setLogoFile(null);
      fetchTestimonials();
    } catch {
      alert("Failed to add testimonial.");
    } finally {
      setSubmitting(false);
    }
  }

  /* ================= UPDATE ================= */
  async function handleUpdate() {
    setSubmitting(true);

    try {
      const supabase = await getSupabase();
      let logoPath = editing.company_logo_url;
      if (editLogoFile) {
        logoPath = await uploadTestimonialImage(editLogoFile);
      }

      const { error } = await supabase
        .schema("api")
        .from("testimonials")
        .update({
          ...editForm,
          company_logo_url: logoPath,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editing.id);

      if (error) throw error;

      setEditing(null);
      setEditLogoFile(null);
      fetchTestimonials();
    } catch {
      alert("Failed to update testimonial.");
    } finally {
      setSubmitting(false);
    }
  }

  async function togglePublish(id, current, caseStudy) {
    if (!current && caseStudy && caseStudy.includes("Publication Authorized: No")) {
      const confirmed = window.confirm(
        "Notice: This submission was marked 'Publication Authorized: No (Private feedback submission only)'. Publishing it publicly without separate written client authorization may violate privacy commitments. Do you wish to proceed?"
      );
      if (!confirmed) return;
    }

    const supabase = await getSupabase();

    await supabase
      .schema("api")
      .from("testimonials")
      .update({ is_published: !current })
      .eq("id", id);

    fetchTestimonials();
  }

  async function deleteTestimonial(id) {
    if (!confirm("Delete this testimonial permanently?")) return;
    const supabase = await getSupabase();
    await supabase.schema("api").from("testimonials").delete().eq("id", id);
    fetchTestimonials();
  }

  async function getPublicLogoUrl(path) {
    if (!path) return null;

    const supabase = await getSupabase();

    return supabase.storage.from("company-logos").getPublicUrl(path).data
      .publicUrl;
  }

  /* ================= UI ================= */
  return (
    <div className="space-y-12">
      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Testimonials</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage client feedback displayed on the website.
        </p>
      </div>

      {/* CREATE CARD */}
      <div className="max-w-2xl rounded-2xl border border-white/20 bg-white/70 p-6 shadow-xl backdrop-blur-xl dark:bg-white/5">
        <h2 className="mb-4 text-lg font-semibold">Add New Testimonial</h2>

        <form onSubmit={handleCreate} className="grid gap-4">
          <input
            placeholder="Company name"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            required
            className="rounded-lg border px-3 py-2 text-sm"
          />

          <input
            placeholder="Service"
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
            required
            className="rounded-lg border px-3 py-2 text-sm"
          />

          <textarea
            placeholder="Remarks"
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            rows={3}
            required
            className="rounded-lg border px-3 py-2 text-sm"
          />

          <input
            placeholder="Case study URL (optional)"
            value={form.case_study}
            onChange={(e) => setForm({ ...form, case_study: e.target.value })}
            className="rounded-lg border px-3 py-2 text-sm"
          />

          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;

              if (file.size > 800 * 1024) {
                alert("Please upload an image smaller than 800KB");
                e.target.value = "";
                return;
              }

              setLogoFile(file);
            }}
            className="text-sm"
          />

          <button
            disabled={submitting}
            className="self-start rounded-xl bg-[rgb(var(--brand-primary))] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:shadow-lg disabled:opacity-60"
          >
            {submitting ? "Adding…" : "Add Testimonial"}
          </button>
        </form>
      </div>

      {/* LIST / SKELETON */}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl border border-white/20 bg-white/70 p-6 shadow-lg backdrop-blur-xl dark:bg-white/5"
            >
              <div className="flex items-start justify-between gap-6">
                <div>
                  <h3 className="text-lg font-semibold">{t.company_name}</h3>
                  <p className="text-sm text-slate-500">{t.service}</p>
                  <p className="mt-2 text-sm">{t.remarks}</p>
                  {t.case_study && (
                    <p className="mt-2 text-xs font-mono text-slate-600 dark:text-slate-400 bg-black/5 dark:bg-white/5 p-2 rounded">
                      {t.case_study}
                    </p>
                  )}
                </div>

                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    t.is_published
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {t.is_published ? "Published" : "Unpublished"}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    setEditing(t);
                    setEditForm({
                      company_name: t.company_name,
                      service: t.service,
                      remarks: t.remarks,
                      case_study: t.case_study || "",
                    });
                  }}
                  className="rounded-lg bg-slate-600 px-4 py-2 text-sm text-white hover:bg-slate-700"
                >
                  Edit
                </button>

                <button
                  onClick={() => togglePublish(t.id, t.is_published, t.case_study)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold ${
                    t.is_published
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-slate-400 text-white hover:bg-slate-500"
                  }`}
                >
                  {t.is_published ? "Published" : "Unpublished"}
                </button>

                <button
                  onClick={() => deleteTestimonial(t.id)}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EDIT MODAL (UNCHANGED) */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold">Edit Testimonial</h2>

            <div className="grid gap-4">
              <input
                value={editForm.company_name}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    company_name: e.target.value,
                  })
                }
                className="rounded border px-3 py-2 text-sm"
              />
              <input
                value={editForm.service}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    service: e.target.value,
                  })
                }
                className="rounded border px-3 py-2 text-sm"
              />
              <textarea
                rows={3}
                value={editForm.remarks}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    remarks: e.target.value,
                  })
                }
                className="rounded border px-3 py-2 text-sm"
              />
              <input
                value={editForm.case_study}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    case_study: e.target.value,
                  })
                }
                className="rounded border px-3 py-2 text-sm"
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  if (file.size > 800 * 1024) {
                    alert("Please upload an image smaller than 800KB");
                    e.target.value = "";
                    return;
                  }

                  setEditLogoFile(file);
                }}
                className="text-sm"
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setEditing(null)}
                className="rounded border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={submitting}
                className="rounded bg-[rgb(var(--brand-primary))] px-4 py-2 text-sm text-white"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= SKELETON ================= */

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-2xl border border-white/20 bg-white/60 p-6 shadow-lg backdrop-blur-xl dark:bg-white/5">
      <div className="mb-3 h-5 w-40 rounded bg-slate-300/60" />
      <div className="mb-4 h-4 w-24 rounded bg-slate-300/50" />
      <div className="mb-2 h-4 w-full rounded bg-slate-300/40" />
      <div className="h-4 w-3/4 rounded bg-slate-300/40" />
    </div>
  );
}
